import { useState, useEffect, useRef } from "react";

const PRIMARY_COLOR = "var(--vz-primary, #f06b2f)";
const PRIMARY_LIGHT = "rgba(240, 107, 47, 0.08)";
const BORDER_LIGHT_BLUE = "#cfd8ea";
const ROW_BORDER_LIGHT_BLUE = "#dde4f0";

export default function SingleSelect({
  name,
  options = [],
  value = "",
  handleChange,
  handleBlur,
  invalid,
  disabled,
  placeholder,
  className,
  style,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        if (handleBlur) handleBlur();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, handleBlur]);

  const selectOption = (opt) => {
    if (handleChange) handleChange(opt);
    setOpen(false);
    if (handleBlur) handleBlur();
  };

  const clearOption = (opt) => {
    if (value === opt && handleChange) handleChange("");
  };

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ position: "relative", ...style }}
    >
      {/* selected chip + arrow */}
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          minHeight: "40px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "6px",
          padding: "4px 36px 4px 10px",
          border: `1px solid ${invalid ? "#f06548" : BORDER_LIGHT_BLUE}`,
          borderRadius: "8px",
          background: disabled ? "#f3f6f9" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
        }}
      >
        {value ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "12px",
              padding: "2px 10px",
              borderRadius: "12px",
              background: "#eef0f4",
              border: "1px solid #dfe3ea",
              color: "#343a40",
            }}
          >
            {value}
          </span>
        ) : placeholder ? (
          <span style={{ fontSize: "13px", color: "#878a99" }}>
            {placeholder}
          </span>
        ) : null}
        <i
          className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "18px",
          }}
        />
      </div>

      {/* options (radio) */}
      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: "4px",
            maxHeight: "220px",
            overflowY: "auto",
            border: `1px solid ${BORDER_LIGHT_BLUE}`,
            borderRadius: "4px",
            background: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {options.map((opt, i) => {
            const checked = value === opt;
            return (
              <div
                key={opt}
                onClick={() => selectOption(opt)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom:
                    i === options.length - 1
                      ? "none"
                      : `1px solid ${ROW_BORDER_LIGHT_BLUE}`,
                  fontSize: "13px",
                  color: "#343a40",
                  background: checked ? PRIMARY_LIGHT : "#fff",
                }}
              >
                {/* radio */}
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: `1.5px solid ${checked ? PRIMARY_COLOR : "#9aa5b5"}`,
                    background: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {checked && (
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: PRIMARY_COLOR,
                      }}
                    />
                  )}
                </span>

                <span style={{ flexGrow: 1 }}>{opt}</span>

                {/* clear this option */}
                <span
                  title="Clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearOption(opt);
                  }}
                  style={{
                    fontSize: "13px",
                    lineHeight: 1,
                    color: "#343a40",
                    padding: "0 2px",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}